import supabase from './supabase';

// Enum for bucket names
export enum BucketName {
  COURSE_VIDEOS = 'course-videos',
  COURSE_IMAGES = 'course-images',
  COURSE_PDFS = 'course-pdfs',
  PROFILE_IMAGES = 'profile-images',
  APP_ASSETS = 'app-assets'
}

// Interface for file metadata (compatibility with existing code)
export interface FileMetadata {
  id?: string;
  bucket_name: string;
  file_path: string;
  original_filename?: string;
  content_type?: string;
  file_size?: number;
  public_url: string;
  related_table?: string;
  related_id?: string;
  related_field?: string;
  description?: string;
  uploaded_by?: string;
  created_at?: string;
  updated_at?: string;
}

// Types for the media files table
export interface MediaFile {
  id?: string;
  file_name: string;
  file_path: string;
  file_data: string;
  content_type?: string;
  file_size?: number;
  category: string;
  related_table?: string;
  related_id?: string;
  related_field?: string;
  description?: string;
  uploaded_by?: string;
  created_at?: string;
  updated_at?: string;
}

// File upload options
export interface UploadOptions {
  category: 'image' | 'video' | 'document' | 'audio' | 'other';
  relatedTable?: string;
  relatedId?: string;
  relatedField?: string;
  description?: string;
}

// File upload utility function to save files to the database
export async function uploadFileToDatabase(
  file: File,
  options: UploadOptions
) {
  try {
    // Convert file to base64 data for storage in database
    const fileReader = new FileReader();
    const fileData = await new Promise<string>((resolve, reject) => {
      fileReader.onload = () => resolve(fileReader.result as string);
      fileReader.onerror = reject;
      fileReader.readAsDataURL(file);
    });
    
    
    
    // Get current user's ID
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    // Calculate file path based on category and filename
    let filePath = '';
    switch (options.category) {
      case 'image':
        filePath = `thumbnails/${file.name}`;
        break;
      case 'video':
        filePath = `videos/${file.name}`;
        break;
      case 'document':
        filePath = `documents/${file.name}`;
        break;
      case 'audio':
        filePath = `audio/${file.name}`;
        break;
      default:
        filePath = `other/${file.name}`;
    }
    
    // Save file data to media_files table
    const { data: mediaData, error: mediaError } = await supabase
      .from("media_files")
      .insert([{
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        content_type: file.type,
        file_data: fileData,
        category: options.category,
        related_table: options.relatedTable,
        related_id: options.relatedId,
        related_field: options.relatedField,
        description: options.description,
        uploaded_by: userId
      }])
      .select();
      
    if (mediaError) {
      
      // Return object URL as fallback
      const tempUrl = URL.createObjectURL(file);
      return { 
        success: false, 
        url: tempUrl, 
        error: mediaError,
        fileData 
      };
    }
    
    // Use the media_files ID as the reference
    const mediaId = mediaData[0].id;
    const mediaUrl = `/api/media/${mediaId}`;
    
    
    
    return { 
      success: true, 
      url: mediaUrl, 
      mediaId, 
      file_path: filePath 
    };
  } catch (err) {
    
    // Create a temporary URL as fallback
    const tempUrl = URL.createObjectURL(file);
    return { 
      success: false, 
      url: tempUrl, 
      error: err 
    };
  }
}

// Function to retrieve a file from the database
export async function getFileFromDatabase(fileId: string) {
  try {
    const { data, error } = await supabase
      .from("media_files")
      .select("*")
      .eq("id", fileId)
      .single();
      
    if (error) {
      
      return { success: false, error };
    }
    
    return { success: true, file: data };
  } catch (err) {
    
    return { success: false, error: err };
  }
}

// Function to get files by related table and ID
export async function getFilesByRelatedRecord(
  relatedTable: string,
  relatedId: string
): Promise<MediaFile[]> {
  try {
    const { data, error } = await supabase
      .from('media_files')
      .select('*')
      .eq('related_table', relatedTable)
      .eq('related_id', relatedId);
      
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    
    return [];
  }
}

// Function to delete a file from the database
export async function deleteFileFromDatabase(fileId: string) {
  try {
    const { error } = await supabase
      .from("media_files")
      .delete()
      .eq("id", fileId);
      
    if (error) {
      
      return { success: false, error };
    }
    
    return { success: true };
  } catch (err) {
    
    return { success: false, error: err };
  }
}

// Legacy function to delete a file (for compatibility with existing code)
export async function deleteFile(
  bucketName: BucketName | string,
  filePath: string
): Promise<{ error: Error | null }> {
  

  try {
    // Try to find the file in the database using the file path
    const path = filePath.startsWith('/') ? filePath.substring(1) : filePath;
    const { data, error } = await supabase
      .from('media_files')
      .select('id')
      .like('file_path', `%${path}%`)
      .limit(1);
      
    if (error) {
      
      return { error: new Error(error.message) };
    }
    
    if (data && data.length > 0) {
      // Delete the file from the database
      const result = await deleteFileFromDatabase(data[0].id);
      if (!result.success) {
        return { error: new Error('Failed to delete file from database') };
      }
      return { error: null };
    }
    
    // Fallback to checking the file_metadata table
    const { data: metadataData, error: metadataError } = await supabase
      .from('file_metadata')
      .select('id')
      .eq('file_path', path)
      .limit(1);
      
    if (metadataError) {
      
      return { error: new Error(metadataError.message) };
    }
    
    if (metadataData && metadataData.length > 0) {
      // Delete the metadata
      const { error: deleteError } = await supabase
        .from('file_metadata')
        .delete()
        .eq('id', metadataData[0].id);
      
      if (deleteError) {
        return { error: new Error(deleteError.message) };
      }
      return { error: null };
    }
    
    // If we couldn't find the file, log it but don't return an error
    
    return { error: null };
  } catch (err) {
    
    return { error: err instanceof Error ? err : new Error('Unknown error deleting file') };
  }
}

// Function to get a file by related field
export async function getFileByField(
  relatedTable: string,
  relatedId: string,
  relatedField: string
): Promise<FileMetadata | null> {
  try {
    // First try to find in media_files table
    const { data: mediaData, error: mediaError } = await supabase
      .from('media_files')
      .select('*')
      .eq('related_table', relatedTable)
      .eq('related_id', relatedId)
      .eq('related_field', relatedField)
      .limit(1);
      
    if (mediaError) {
      
    } else if (mediaData && mediaData.length > 0) {
      // Convert media_files record to FileMetadata format
      const media = mediaData[0];
      return {
        id: media.id,
        bucket_name: media.category || 'media_files',
        file_path: media.file_path,
        original_filename: media.file_name,
        content_type: media.content_type,
        file_size: media.file_size,
        public_url: `/api/media/${media.id}`,
        related_table: media.related_table,
        related_id: media.related_id,
        related_field: media.related_field,
        description: media.description,
        uploaded_by: media.uploaded_by,
        created_at: media.created_at,
        updated_at: media.updated_at
      };
    }
    
    // Fallback to checking file_metadata table
    const { data, error } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('related_table', relatedTable)
      .eq('related_id', relatedId)
      .eq('related_field', relatedField)
      .limit(1);
    
    if (error) {
      
      return null;
    }
    
    if (data && data.length > 0) {
      return data[0] as FileMetadata;
    }
    
    return null;
  } catch (err) {
    
    return null;
  }
}

// Function to initialize storage buckets (stub for compatibility)
export async function initializeStorageBuckets() {
  // Storage buckets initialization bypassed - using database storage instead
  return Promise.resolve();
}

// Function that would have been used to ensure a bucket exists (stub for compatibility)
export async function ensureBucketExists(bucketName: string) {
  // Bucket operations bypassed for this bucket - using database storage instead
  return Promise.resolve(true);
}

// Legacy file upload function (stub for compatibility)
export async function uploadFile(
  file: File,
  bucketName: BucketName | string,
  path: string = ''
): Promise<{ data: FileMetadata | null; error: Error | null }> {
  
  
  // Determine category based on bucket name
  let category: 'image' | 'video' | 'document' | 'audio' | 'other' = 'other';
  
  if (typeof bucketName === 'string') {
    if (bucketName.includes('image')) category = 'image';
    else if (bucketName.includes('video')) category = 'video';
    else if (bucketName.includes('pdf')) category = 'document';
  } else {
    switch (bucketName) {
      case BucketName.COURSE_IMAGES:
        category = 'image';
        break;
      case BucketName.COURSE_VIDEOS:
        category = 'video';
        break;
      case BucketName.COURSE_PDFS:
        category = 'document';
        break;
      case BucketName.PROFILE_IMAGES:
        category = 'image';
        break;
      default:
        category = 'other';
    }
  }
  
  // Create the file path
  const filePath = path ? path : `${bucketName}/${file.name}`;
  
  try {
    // Use the new database upload function
    const result = await uploadFileToDatabase(file, {
      category,
      description: `Uploaded to ${bucketName}/${path}`
    });
    
    if (!result.success) {
      return { data: null, error: new Error(result.error?.message || 'Upload failed') };
    }
    
    // Get current user's ID for uploaded_by field
    let userId: string | undefined;
    try {
      const { data: userData } = await supabase.auth.getUser();
      userId = userData?.user?.id;
    } catch (error) {
      
    }
    
    // Convert to FileMetadata format for compatibility
    const fileMetadata: FileMetadata = {
      id: result.mediaId,
      bucket_name: typeof bucketName === 'string' ? bucketName : bucketName.toString(),
      file_path: filePath,
      original_filename: file.name,
      content_type: file.type,
      file_size: file.size,
      public_url: result.url,
      related_table: undefined,
      related_id: undefined,
      uploaded_by: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    return { data: fileMetadata, error: null };
  } catch (err) {
    
    return { data: null, error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

// Legacy function to get a signed URL (stub for compatibility)
export async function getSignedUrl(
  bucketName: BucketName | string,
  filePath: string,
  expiresIn = 60 // Seconds
): Promise<{ signedUrl: string | null; error: Error | null }> {
  
  
  try {
    // Try to find the file in the media_files table based on the path pattern
    const path = filePath.startsWith('/') ? filePath.substring(1) : filePath;
    const { data, error } = await supabase
      .from('media_files')
      .select('id')
      .like('file_path', `%${path}%`)
      .limit(1);
      
    if (error) {
      
      return { signedUrl: null, error: new Error(error.message) };
    }
    
    if (data && data.length > 0) {
      // Return API endpoint URL that can serve the file
      return { 
        signedUrl: `/api/media/${data[0].id}?expires=${Date.now() + (expiresIn * 1000)}`, 
        error: null 
      };
    }
    
    // Fallback to checking if there might be a public URL in the file_metadata table
    const { data: metadataData, error: metadataError } = await supabase
      .from('file_metadata')
      .select('public_url')
      .eq('file_path', path)
      .limit(1);
      
    if (metadataError) {
      
      return { signedUrl: null, error: new Error(metadataError.message) };
    }
    
    if (metadataData && metadataData.length > 0 && metadataData[0].public_url) {
      return { signedUrl: metadataData[0].public_url, error: null };
    }
    
    return { 
      signedUrl: null, 
      error: new Error(`File not found in database: ${path}`) 
    };
  } catch (err) {
    
    return { 
      signedUrl: null, 
      error: err instanceof Error ? err : new Error('Unknown error generating signed URL') 
    };
  }
}

export default {
  uploadFileToDatabase,
  getFileFromDatabase,
  getFilesByRelatedRecord,
  deleteFileFromDatabase,
  uploadFile,
  deleteFile,
  getFileByField,
  initializeStorageBuckets,
  ensureBucketExists,
  getSignedUrl
};
